import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useSelector, useDispatch } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { AuthContext } from '../../../../Header/auth - context';
import CustomDropdown from '../../../../custom/customDropdown';
import { exportAetrPotvrdaPdf } from './aetrPotvrdaPdf';

const emptyForm = {
    id_zaposleni: null,
    TipAETR: null,
    VremeOd: null,
    VremeDo: null,
    DatumIzdavanja: null,
    Napomena: ''
};

const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + 'Z';
};

const ZaposleniAETRPotvrda = () => {
  const { t } = useTranslation();

    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    /* Zaposleni izabran u Pregledu (ili na bilo kom drugom tabu) — nosi se
       kroz sve tabove, pa se ovde nudi kao već izabran. */
    const employee = useSelector(state => state.employee);
    const dispatch = useDispatch();
    const toast = useRef(null);

    const [employeesOptions, setEmployeesOptions] = useState([]);
    const [tipAetrOptions, setTipAetrOptions] = useState([]);
    const [podaci, setPodaci] = useState([]);
    const [formData, setFormData] = useState(emptyForm);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const handleErrors = (response) => {
        if (response.ok) {
            return response.json();
        } else {
            throw response;
        }
    };

    const showError = (errorMessage) => {
        toast.current.show({
            severity: 'error',
            summary: t('admin.m_greska'),
            detail: errorMessage?.message || 'Greška na serveru.',
            life: 3000
        });
    };

    const loadInit = () => {
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + auth.token
            }
        };
        fetch(process.env.REACT_APP_BACKEND_URL + '/employees/AETR/init', requestOptions)
            .then(handleErrors)
            .then((data) => {
                setEmployeesOptions(data.EmployeesDB || []);
                setTipAetrOptions(data.TipoviAETR || []);
                setPodaci(data.PodaciDB || []);
            })
            .catch((error) => {
                if (error.json) {
                    error.json().then(showError);
                } else {
                    showError(error);
                }
            });
    };

    useEffect(() => {
        if (!formSubmitted) {
            loadInit();
        }
    }, [formSubmitted]);

    /* Prethodno izabrani zaposleni upada u formu čim spisak stigne — samo ako
       u formi već nema izbora (da ne pregazi zaposlenog iz reda otvorenog za
       izmenu) i samo ako je među ponuđenima (tuđa firma, pasivan). */
    useEffect(() => {
        if (!employee || employeesOptions.length === 0) return;
        const opcija = employeesOptions.find(o => String(o.value) === String(employee));
        if (!opcija) return;
        setFormData(prev => (prev.id_zaposleni ? prev : { ...prev, id_zaposleni: opcija.value }));
    }, [employee, employeesOptions]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        /* Izbor zaposlenog ovde menja i zajednički izbor, da ga ostali tabovi
           zateknu istog. */
        if (name === 'id_zaposleni') {
            dispatch({ type: 'EMPLOYEE', value: value ?? null });
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                /* Red nosi svog zaposlenog — on postaje i zajednički izbor. */
                dispatch({ type: 'EMPLOYEE', value: row.id_zaposleni ?? null });
                setFormData({
                    id_zaposleni: row.id_zaposleni,
                    TipAETR: row.TipAETR,
                    VremeOd: row.VremeOdRaw ? new Date(row.VremeOdRaw) : null,
                    VremeDo: row.VremeDoRaw ? new Date(row.VremeDoRaw) : null,
                    DatumIzdavanja: row.DatumIzdavanjaRaw ? new Date(row.DatumIzdavanjaRaw) : null,
                    Napomena: row.Napomena || ''
                });
            } else {
                setFormData(emptyForm);
            }
            return next;
        });
    };

    const resetForm = () => {
        setSelectedId(null);
        setFormData(emptyForm);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();

        if (!formData.id_zaposleni || !formData.TipAETR || !formData.VremeOd || !formData.VremeDo || !formData.DatumIzdavanja) {
            toast.current.show({
                severity: 'warn',
                summary: t('admin.m_upozorenje'),
                detail: t('admin.m_popuni_sva_obavezna_polja'),
                life: 3000
            });
            return;
        }

        const body = {
            id: selectedId || null,
            id_zaposleni: formData.id_zaposleni,
            TipAETR: formData.TipAETR,
            VremeOd: toUtcIso(formData.VremeOd),
            VremeDo: toUtcIso(formData.VremeDo),
            DatumIzdavanja: toUtcIso(formData.DatumIzdavanja),
            Napomena: formData.Napomena
        };

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + auth.token
            },
            body: JSON.stringify(body)
        };

        setFormSubmitted(true);
        fetch(process.env.REACT_APP_BACKEND_URL + '/employees/AETR/save', requestOptions)
            .then(handleErrors)
            .then((data) => {
                toast.current.show({
                    severity: 'success',
                    summary: t('admin.m_uspesno'),
                    detail: data.message,
                    life: 3000
                });
                resetForm();
                loadInit();
                setFormSubmitted(false);
            })
            .catch((error) => {
                setFormSubmitted(false);
                if (error.json) {
                    error.json().then(showError);
                } else {
                    showError(error);
                }
            });
    };

    const handleDelete = () => {
        if (!selectedId) return;
        confirmDialog({
            message: 'Da li sigurno želite da obrišete izabranu AETR potvrdu?',
            header: 'Potvrda brisanja',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Da',
            rejectLabel: 'Ne',
            accept: () => {
                const requestOptions = {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + auth.token
                    },
                    body: JSON.stringify({ id: selectedId })
                };
                fetch(process.env.REACT_APP_BACKEND_URL + '/employees/AETR/delete', requestOptions)
                    .then(handleErrors)
                    .then((data) => {
                        toast.current.show({
                            severity: 'success',
                            summary: t('admin.m_uspesno'),
                            detail: data.message,
                            life: 3000
                        });
                        resetForm();
                        loadInit();
                    })
                    .catch((error) => {
                        if (error.json) {
                            error.json().then(showError);
                        } else {
                            showError(error);
                        }
                    });
            }
        });
    };

    const handleDownloadPdf = (rowData) => {
        exportAetrPotvrdaPdf(rowData.id, auth.token, `AETR_Potvrda_${rowData.Vozac}.pdf`)
            .catch((error) => {
                toast.current.show({
                    severity: 'error',
                    summary: t('admin.m_greska'),
                    detail: error.message,
                    life: 3000
                });
            });
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('AETR Potvrde');
        const headers = ['Vozač', 'Tip odsustva', 'Operater', 'Datum od', 'Datum do', 'Datum izdavanja', 'Datum kreiranja', 'Napomena'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (podaci || []).forEach(r => {
            const row = worksheet.addRow([r.Vozac, r.TipOdsustva, r.Operater, r.DatumOd, r.DatumDo, r.DatumIzdavanja, r.DatumEntered, r.Napomena]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `AETR Potvrde - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form onSubmit={handleFormSubmit}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                            <div style={{ flex: '1 1 30%', minWidth: 0, maxWidth: '30%', display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="id_zaposleni">{t('finansije.zaposleni')}:</label>
                                <CustomDropdown
                                    id="id_zaposleni"
                                    name="id_zaposleni"
                                    style={{ width: '100%' }}
                                    value={formData.id_zaposleni}
                                    options={employeesOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder={t('finansije.izaberiZaposlenog')}
                                    onChange={handleChange}
                                />
                            </div>
                            <div style={{ flex: '1 1 30%', minWidth: 0, maxWidth: '30%', display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="TipAETR">{t('admin.tipAetr')}:</label>
                                <CustomDropdown
                                    id="TipAETR"
                                    name="TipAETR"
                                    style={{ width: '100%' }}
                                    value={formData.TipAETR}
                                    options={tipAetrOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder={t('admin.phTipPotvrde')}
                                    onChange={handleChange}
                                />
                            </div>
                            <div style={{ flex: '1 1 30%', minWidth: 0, maxWidth: '30%' }}></div>

                            <div style={{ flex: '1 1 30%', minWidth: 0, maxWidth: '30%', display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="VremeOd">{t('admin.vremeOd')}:</label>
                                <Calendar id="VremeOd" name="VremeOd" value={formData.VremeOd} onChange={handleChange}
                                    style={{ width: '100%' }} inputStyle={{ width: '100%' }}
                                    showIcon showTime hourFormat="24" dateFormat="dd.mm.yy" mask="99.99.9999 99:99" />
                            </div>
                            <div style={{ flex: '1 1 30%', minWidth: 0, maxWidth: '30%', display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="VremeDo">{t('admin.vremeDo')}:</label>
                                <Calendar id="VremeDo" name="VremeDo" value={formData.VremeDo} onChange={handleChange}
                                    style={{ width: '100%' }} inputStyle={{ width: '100%' }}
                                    showIcon showTime hourFormat="24" dateFormat="dd.mm.yy" mask="99.99.9999 99:99" />
                            </div>
                            <div style={{ flex: '1 1 30%', minWidth: 0, maxWidth: '30%', display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="DatumIzdavanja">{t('admin.datumIzdavanja')}:</label>
                                <Calendar id="DatumIzdavanja" name="DatumIzdavanja" value={formData.DatumIzdavanja} onChange={handleChange}
                                    style={{ width: '100%' }} inputStyle={{ width: '100%' }}
                                    showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                            </div>

                            <div style={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column' }}>
                                <label htmlFor="Napomena">{t('nova.napomena')}:</label>
                                <InputTextarea id="Napomena" name="Napomena" value={formData.Napomena} onChange={handleChange} rows={3} autoResize />
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button type="submit" onClick={handleFormSubmit} style={{ minWidth: 'fit-content', marginTop: '2%' }} className="btn btn-success">
                                {selectedId ? 'SAČUVAJ' : 'UNOS'}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable
                        value={podaci || []}
                        paginator rows={10}
                        globalFilter={globalFilter}
                        sortField="id" sortOrder={-1}
                        scrollable scrollHeight="50vh"
                        header={tableHeader}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        rowClassName={r => String(r.id) === String(selectedId) ? { 'selected-row': true } : {}}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column header={t('admin.pdf')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button icon="pi pi-file-pdf" severity="danger" rounded type="button"
                                    onClick={() => handleDownloadPdf(rowData)} data-pr-tooltip={t('admin.skidanjePdf')} />
                            )} />
                        <Column field="Vozac" header={t('ture.vozac')} sortable style={{ minWidth: '160px' }} />
                        <Column field="TipOdsustva" header={t('admin.tipOdsustva')} sortable style={{ minWidth: '220px' }} />
                        <Column field="Operater" header={t('admin.operater')} sortable style={{ minWidth: '160px' }} />
                        <Column field="DatumOd" header={t('ture.datumOd')} sortable style={{ minWidth: '140px' }} />
                        <Column field="DatumDo" header={t('ture.datumDo')} sortable style={{ minWidth: '140px' }} />
                        <Column field="DatumIzdavanja" header={t('admin.datumIzdavanja')} sortable style={{ minWidth: '140px' }} />
                        <Column field="DatumEntered" header={t('nova.datumKreiranja')} sortable style={{ minWidth: '140px' }} />
                        <Column field="Napomena" header={t('nova.napomena')} style={{ minWidth: '200px' }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default ZaposleniAETRPotvrda;
